import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Calendar,
  Tag,
  User,
  Film,
  X,
  Clock,
  Share2,
  Check,
} from 'lucide-react';

export default function JubileeVideoPlayerModal({ video, show, onHide, onSelectVideo, allVideos = [] }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (show && videoRef.current) {
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [video, show]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().then(() => setIsPlaying(true)).catch((e) => console.log('Play prevented', e));
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration || 0);
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      setIsMuted(val === 0);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    if (isMuted) {
      videoRef.current.muted = false;
      setIsMuted(false);
      videoRef.current.volume = volume || 0.5;
    } else {
      videoRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (secs) => {
    if (isNaN(secs)) return '00:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (!video) return null;

  const relatedVideos = allVideos
    .filter((v) => v.id !== video.id && (v.categoryId === video.categoryId || v.eventId === video.eventId))
    .slice(0, 3);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered contentClassName="border-0 shadow-2xl rounded-4 overflow-hidden">
      <div className="bg-dark text-white p-3 d-flex justify-content-between align-items-center border-bottom border-secondary border-opacity-25">
        <div className="d-flex align-items-center gap-2">
          <Film className="text-warning" size={20} />
          <span className="fw-bold fs-6 text-truncate" style={{ maxWidth: '650px' }}>
            {video.title}
          </span>
        </div>
        <Button variant="link" className="text-white-50 p-1 hover:text-white" onClick={onHide}>
          <X size={22} />
        </Button>
      </div>

      <div className="modal-body p-0 bg-black">
        {/* Video Player Container */}
        <div ref={containerRef} className="position-relative bg-black" style={{ maxHeight: '65vh' }}>
          <video
            ref={videoRef}
            src={video.fileUrl}
            poster={video.thumbnailUrl}
            className="w-100 d-block"
            style={{ maxHeight: '65vh', objectFit: 'contain' }}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleTimeUpdate}
            onEnded={() => setIsPlaying(false)}
            onClick={togglePlay}
            playsInline
          />

          {/* Big center play icon if paused */}
          {!isPlaying && (
            <div
              className="position-absolute top-50 start-50 translate-middle rounded-circle d-flex align-items-center justify-content-center shadow-lg cursor-pointer"
              style={{ width: '70px', height: '70px', background: 'rgba(212, 175, 55, 0.9)', color: '#fff' }}
              onClick={togglePlay}
            >
              <Play size={32} style={{ marginLeft: '4px' }} fill="#fff" />
            </div>
          )}

          {/* Player Controls Bar */}
          <div
            className="position-absolute bottom-0 start-0 end-0 p-3"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)' }}
          >
            {/* Seek bar */}
            <input
              type="range"
              min="0"
              max={duration || 100}
              step="0.1"
              value={currentTime}
              onChange={handleSeek}
              className="form-range w-100 mb-2 cursor-pointer"
              style={{ accentColor: '#d4af37', height: '5px' }}
            />

            <div className="d-flex align-items-center justify-content-between text-white fs-6">
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-sm text-white p-0 border-0 d-flex align-items-center"
                  onClick={togglePlay}
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </button>

                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-sm text-white p-0 border-0" onClick={toggleMute}>
                    {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="form-range"
                    style={{ width: '70px', accentColor: '#d4af37' }}
                  />
                </div>

                <span className="small text-white-50" style={{ fontFamily: 'monospace' }}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              <div className="d-flex align-items-center gap-3">
                {/* Speed selector */}
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-outline-secondary text-white py-0 px-2 rounded small"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    {playbackSpeed}x
                  </button>
                  <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end shadow">
                    {[0.75, 1, 1.25, 1.5, 2].map((s) => (
                      <li key={s}>
                        <button
                          className={`dropdown-item small ${playbackSpeed === s ? 'active bg-success' : ''}`}
                          onClick={() => handleSpeedChange(s)}
                        >
                          {s}x
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  className="btn btn-sm text-white p-0 border-0"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Video Info Section */}
        <div className="p-4 bg-white text-dark">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Badge bg="danger" className="px-2 py-1">
                  Video Archive
                </Badge>
                {video.categoryName && (
                  <Badge bg="light" text="dark" className="border">
                    {video.categoryName}
                  </Badge>
                )}
                {video.eventName && (
                  <Badge bg="success" className="bg-opacity-10 text-success border border-success border-opacity-25">
                    {video.eventName}
                  </Badge>
                )}
                <Badge bg="secondary" className="bg-opacity-75">
                  Year {video.year}
                </Badge>
              </div>

              <h4 className="fw-bold text-slate-900 mb-2">{video.title}</h4>
              <p className="text-muted small mb-3" style={{ lineHeight: '1.6' }}>
                {video.description}
              </p>

              {/* Speaker / Metadata tags */}
              <div className="p-3 bg-light rounded-3 border d-flex flex-wrap gap-3 small text-secondary">
                {video.speaker && (
                  <div className="d-flex align-items-center gap-1">
                    <User size={15} className="text-primary" />
                    <strong>Speaker:</strong> {video.speaker}
                  </div>
                )}
                {video.date && (
                  <div className="d-flex align-items-center gap-1">
                    <Calendar size={15} className="text-success" />
                    <strong>Date:</strong> {video.date}
                  </div>
                )}
                {video.duration && (
                  <div className="d-flex align-items-center gap-1">
                    <Clock size={15} className="text-warning" />
                    <strong>Duration:</strong> {video.duration}
                  </div>
                )}
                {video.source && (
                  <div className="d-flex align-items-center gap-1">
                    <strong>Source:</strong> {video.source}
                  </div>
                )}
              </div>

              {/* Tags */}
              {video.tags && video.tags.length > 0 && (
                <div className="mt-3 d-flex align-items-center flex-wrap gap-1">
                  <Tag size={14} className="text-muted me-1" />
                  {video.tags.map((t, idx) => (
                    <span key={idx} className="badge bg-light text-secondary border px-2 py-1">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Related Videos sidebar */}
            <div className="col-lg-4 border-start">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="fw-bold m-0 text-dark">Related Jubilee Videos</h6>
                <Button variant="outline-secondary" size="sm" onClick={handleCopyLink} className="d-flex align-items-center gap-1 py-1 px-2 text-xs">
                  {copied ? <Check size={14} className="text-success" /> : <Share2 size={14} />}
                  <span>{copied ? 'Copied' : 'Share'}</span>
                </Button>
              </div>

              {relatedVideos.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {relatedVideos.map((rv) => (
                    <div
                      key={rv.id}
                      className="d-flex gap-2 p-2 rounded-3 border hover-bg-light cursor-pointer align-items-center"
                      onClick={() => onSelectVideo && onSelectVideo(rv)}
                      style={{ cursor: 'pointer', background: '#f8fafc' }}
                    >
                      <img
                        src={rv.thumbnailUrl}
                        alt={rv.title}
                        className="rounded object-fit-cover"
                        style={{ width: '60px', height: '42px' }}
                      />
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="small fw-semibold text-dark text-truncate">{rv.title}</div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          {rv.year} • {rv.duration || 'Video'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted small">No other related videos found for this collection.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
